import * as React from "react";
import { cn } from "../../../lib/utils";

export interface EditorHandle {
  insertAtCursor: (text: string) => void;
  focus: () => void;
}

export interface HtmlCodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const HtmlCodeEditor = React.forwardRef<EditorHandle, HtmlCodeEditorProps>(
  ({ value, onChange, placeholder, className }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const gutterRef = React.useRef<HTMLDivElement>(null);

    React.useImperativeHandle(ref, () => ({
      insertAtCursor: (text: string) => {
        const el = textareaRef.current;
        if (!el) return;
        const start = el.selectionStart ?? value.length;
        const end = el.selectionEnd ?? value.length;
        const next = value.slice(0, start) + text + value.slice(end);
        onChange(next);
        requestAnimationFrame(() => {
          el.focus();
          const caret = start + text.length;
          el.setSelectionRange(caret, caret);
        });
      },
      focus: () => textareaRef.current?.focus(),
    }));

    const lineCount = Math.max(1, value.split("\n").length);
    const lineNumbers = React.useMemo(
      () => Array.from({ length: lineCount }, (_, i) => i + 1).join("\n"),
      [lineCount],
    );

    const handleScroll = () => {
      if (gutterRef.current && textareaRef.current) {
        gutterRef.current.scrollTop = textareaRef.current.scrollTop;
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Tab") {
        e.preventDefault();
        const el = e.currentTarget;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const next = value.slice(0, start) + "  " + value.slice(end);
        onChange(next);
        requestAnimationFrame(() => {
          el.setSelectionRange(start + 2, start + 2);
        });
      }
    };

    return (
      <div
        className={cn(
          "relative flex h-full w-full overflow-hidden rounded-lg bg-sunken font-mono text-sm",
          className,
        )}
      >
        <div
          ref={gutterRef}
          aria-hidden
          className="select-none overflow-hidden whitespace-pre bg-sunken px-4 py-3 text-right text-text-tertiary"
        >
          {lineNumbers}
        </div>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          spellCheck={false}
          className="h-full flex-1 resize-none bg-transparent px-4 py-3 text-slate-900 outline-none placeholder:text-slate-400 dark:text-slate-100 dark:placeholder:text-slate-600"
        />
      </div>
    );
  },
);

HtmlCodeEditor.displayName = "HtmlCodeEditor";
